import { useState, useEffect, useCallback, useRef } from "react";
import type { Route } from "./+types/cara.demo";
import { useNavigate, useParams, useLoaderData } from "react-router";
import { CaraStudio } from "~/components/cara/CaraStudio";
import { DemoGallery } from "~/components/cara/DemoGallery";
import { CaratzonLogo } from "~/components/marketing/svg/CaratzonLogo";
import { BrilliantCut } from "~/components/marketing/svg/BrilliantCut";
import { useT } from "~/i18n/context";
import { detectLocale } from "~/i18n/index";
import { translations } from "~/i18n/index";
import type { Locale } from "~/i18n/index";
import { LanguageSwitcher } from "~/components/marketing/LanguageSwitcher";

export type DemoAudience = "designer" | "brands";

export async function loader({ request, params }: Route.LoaderArgs) {
  const url = new URL(request.url);
  const demoAudience: DemoAudience = url.searchParams.get("for") === "brands" ? "brands" : "designer";

  const locale = detectLocale(
    request.headers.get("Cookie"),
    request.headers.get("Accept-Language"),
  );

  type RestoredArtifact = { id: string; type: string; data: Record<string, unknown>; imageUrl?: string; createdAt: string };
  let restoredUIMessages: unknown[] | undefined;
  let restoredArtifacts: RestoredArtifact[] = [];
  let restoredCreatorName = "";

  if (params.sessionId) {
    try {
      const { loadDemoSessionMessages, loadDemoSessionArtifacts, loadDemoSessionUIMessages } = await import("@caratzon/server");

      const [uiMessages, result, artifacts] = await Promise.all([
        loadDemoSessionUIMessages(params.sessionId),
        loadDemoSessionMessages(params.sessionId),
        loadDemoSessionArtifacts(params.sessionId),
      ]);

      if (uiMessages) {
        restoredUIMessages = uiMessages;
      }

      if (result) {
        restoredCreatorName = result.session.creatorName ?? "";
      }
      restoredArtifacts = artifacts;
    } catch (err) {
      console.error("[cara.demo] Failed to restore session:", err);
    }
  }

  return { locale, demoAudience, restoredUIMessages, restoredArtifacts, restoredCreatorName };
}

export function meta({ data }: Route.MetaArgs) {
  const locale = (data?.locale ?? "en") as Locale;
  const t = translations[locale];
  return [
    { title: t.demo.meta.title },
    { name: "description", content: t.demo.meta.description },
    { name: "robots", content: "noindex" },
  ];
}

const PROMPT_ICONS = ["ring", "gem", "bracelet", "cuff"] as const;

function PromptIcon({ type }: { type: string }) {
  const paths: Record<string, React.ReactNode> = {
    ring: <><circle cx="12" cy="12" r="7" /><path d="M9 5.5 12 2l3 3.5" /></>,
    gem: <path d="M6 3h12l4 6-10 13L2 9Z" />,
    bracelet: <><ellipse cx="12" cy="12" rx="9" ry="6" /><path d="M3 12c0-2 1.5-4 3-5" /><path d="M21 12c0-2-1.5-4-3-5" /></>,
    cuff: <><path d="M4 8c0-2.2 3.6-4 8-4s8 1.8 8 4" /><path d="M4 8v8c0 2.2 3.6 4 8 4s8-1.8 8-4V8" /></>,
  };
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
      {paths[type] ?? <circle cx="12" cy="12" r="4" />}
    </svg>
  );
}

function SplashScreen({ onStart, demoAudience = "designer" }: { onStart: (name: string, prompt?: string) => void; demoAudience?: DemoAudience }) {
  const { t } = useT();
  const base = t.demo;
  const brandOverrides = base.brands;
  const d = demoAudience === "brands" && brandOverrides
    ? {
        ...base,
        splash: { ...base.splash, ...brandOverrides.splash },
        prompts: brandOverrides.prompts ?? base.prompts,
        capabilities: brandOverrides.capabilities ?? base.capabilities,
        header: { ...base.header, ...brandOverrides.header },
      }
    : base;
  const [mounted, setMounted] = useState(false);
  const [activeCapability, setActiveCapability] = useState(0);
  const [name, setName] = useState("");

  useEffect(() => {
    requestAnimationFrame(() => setMounted(true));
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveCapability((prev) => (prev + 1) % d.capabilities.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [d.capabilities.length]);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Enter" && !e.metaKey && !e.ctrlKey && !(e.target instanceof HTMLInputElement)) {
        onStart(name.trim());
      }
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onStart, name]);

  const stagger = (i: number) => ({
    opacity: mounted ? 1 : 0,
    transform: mounted ? "translateY(0)" : "translateY(12px)",
    transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${150 + i * 100}ms, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${150 + i * 100}ms`,
  });

  return (
    <div
      data-theme="client"
      className="min-h-screen bg-background text-foreground flex flex-col relative overflow-hidden"
    >
      {/* Ambient diamond */}
      <div
        className="fixed inset-0 flex items-center justify-center pointer-events-none"
        aria-hidden="true"
        style={{ opacity: mounted ? 1 : 0, transition: "opacity 1.5s ease 0.3s" }}
      >
        <BrilliantCut size={1000} opacity={0.018} animated />
      </div>

      <div
        className="fixed inset-0 pointer-events-none"
        aria-hidden="true"
        style={{ background: "radial-gradient(ellipse at 50% 30%, oklch(0.45 0.08 213 / 0.03), transparent 70%)" }}
      />

      <header
        className="relative flex items-center justify-between px-6 lg:px-12 pt-7 pb-4 z-10"
        style={stagger(0)}
      >
        <a href="/" className="transition-opacity hover:opacity-60">
          <CaratzonLogo size={36} className="opacity-80" />
        </a>
        <div className="flex items-center gap-4">
          <LanguageSwitcher />
          <a
            href="/sign-in"
            className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground/60 hover:text-foreground transition-colors"
          >
            {d.splash.signIn}
          </a>
        </div>
      </header>

      <main className="relative flex-1 flex flex-col items-center justify-center px-6 py-12 lg:py-16 z-10">
        <div className="w-full max-w-2xl text-center">
          <div style={stagger(1)} className="inline-flex items-center justify-center mb-8">
            <div
              className="h-14 w-14 rounded-2xl flex items-center justify-center"
              style={{ background: "oklch(0.45 0.08 213 / 0.08)" }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="h-7 w-7" style={{ color: "var(--teal-sapphire)" }}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456Z" />
              </svg>
            </div>
          </div>

          <p
            style={{ ...stagger(2), color: "var(--teal-sapphire)" }}
            className="text-[10px] uppercase tracking-[0.3em] mb-5 font-medium"
          >
            {d.splash.eyebrow}
          </p>

          <h1
            style={{ ...stagger(3), fontSize: "clamp(2.2rem, 5.5vw, 3.6rem)" }}
            className="font-display font-light text-foreground leading-[1.04] mb-3"
          >
            {d.splash.heading}
          </h1>

          <p
            style={{ ...stagger(4), fontSize: "clamp(0.88rem, 1.8vw, 1rem)" }}
            className="text-muted-foreground leading-relaxed max-w-md mx-auto mb-8"
          >
            {d.splash.subheading}
          </p>

          {/* Name input */}
          <div style={stagger(5)} className="mb-8 max-w-xs mx-auto">
            <div className="relative">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={d.splash.namePlaceholder}
                maxLength={40}
                className="w-full text-center text-sm bg-transparent border-b border-border/50 focus:border-foreground/30 outline-none py-2.5 px-3 text-foreground placeholder:text-muted-foreground/30 transition-colors"
              />
            </div>
            <p className="text-[10px] text-muted-foreground/30 mt-2">{d.splash.nameHint}</p>
          </div>

          <div style={stagger(6)} className="mb-10 h-6 relative overflow-hidden">
            {d.capabilities.map((cap, i) => (
              <div
                key={cap.label}
                className="absolute inset-0 flex items-center justify-center gap-2 transition-all duration-500"
                style={{
                  opacity: activeCapability === i ? 1 : 0,
                  transform: activeCapability === i ? "translateY(0)" : "translateY(8px)",
                }}
              >
                <span
                  className="inline-block h-1 w-1 rounded-full"
                  style={{ background: "var(--teal-sapphire)" }}
                />
                <span className="text-xs text-foreground/80 font-medium">{cap.label}</span>
                <span className="text-xs text-muted-foreground/60">{cap.desc}</span>
              </div>
            ))}
          </div>

          <div style={stagger(7)}>
            <button
              onClick={() => onStart(name.trim())}
              className="group inline-flex items-center gap-2.5 px-10 py-4 text-[11px] uppercase tracking-[0.22em] font-medium transition-all duration-200 hover:brightness-110 active:scale-[0.98]"
              style={{
                background: "var(--teal-sapphire)",
                color: "var(--teal-sapphire-fg, #fff)",
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z" />
              </svg>
              {d.splash.cta}
            </button>
            <p className="mt-3 text-[10px] text-muted-foreground/40">
              {(() => {
                const [before, after] = d.splash.enterHint.split("{key}");
                return (
                  <>
                    {before}
                    <kbd className="inline-block px-1.5 py-0.5 rounded border border-border/50 text-[9px] font-mono mx-0.5">Enter</kbd>
                    {after}
                  </>
                );
              })()}
            </p>
          </div>

          <div style={stagger(8)} className="mt-12 mb-8 flex items-center gap-4 max-w-md mx-auto">
            <div className="flex-1 h-px bg-border/40" />
            <span className="text-[9px] uppercase tracking-[0.25em] text-muted-foreground/30 font-medium">{d.splash.promptDivider}</span>
            <div className="flex-1 h-px bg-border/40" />
          </div>

          <div style={stagger(9)} className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-w-lg mx-auto">
            {d.prompts.map((prompt, i) => (
              <button
                key={prompt.label}
                onClick={() => onStart(name.trim(), prompt.text)}
                className="group flex items-start gap-3 text-left rounded-xl border border-border/40 px-4 py-3.5 transition-all duration-200 hover:border-border hover:bg-card/50 hover:shadow-sm"
              >
                <div
                  className="mt-0.5 h-7 w-7 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-200"
                  style={{ background: "oklch(0.45 0.08 213 / 0.06)", color: "var(--teal-sapphire)" }}
                >
                  <PromptIcon type={PROMPT_ICONS[i] ?? "ring"} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-foreground mb-0.5 group-hover:text-foreground/90">{prompt.label}</p>
                  <p className="text-[11px] text-muted-foreground/60 leading-relaxed line-clamp-2">{prompt.text}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </main>

      <footer className="relative z-10 px-6 py-6 text-center" style={stagger(10)}>
        <div className="flex flex-wrap items-center justify-center gap-4 text-[10px] text-muted-foreground/40">
          <span>{d.splash.noAccount}</span>
          <span className="h-3 w-px bg-border/30" />
          <span>{d.splash.notSaved}</span>
          <span className="h-3 w-px bg-border/30" />
          <a href={demoAudience === "brands" ? "/sign-in?role=buyer" : "/studio"} className="hover:text-foreground transition-colors">{d.splash.fullAccess}</a>
        </div>
      </footer>
    </div>
  );
}

type DemoTab = "create" | "gallery";

function TabIcon({ tab, active }: { tab: DemoTab; active: boolean }) {
  const cls = `h-3.5 w-3.5 transition-colors ${active ? "" : "opacity-50"}`;
  if (tab === "create") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className={cls}>
        <path d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className={cls}>
      <rect width="7" height="7" x="3" y="3" rx="1" />
      <rect width="7" height="7" x="14" y="3" rx="1" />
      <rect width="7" height="7" x="3" y="14" rx="1" />
      <rect width="7" height="7" x="14" y="14" rx="1" />
    </svg>
  );
}

export default function CaraDemoPage() {
  const { t } = useT();
  const base = t.demo;
  const loaderData = useLoaderData<typeof loader>();
  const demoAudience = (loaderData.demoAudience ?? "designer") as DemoAudience;
  const brandOverrides = base.brands;
  const d = demoAudience === "brands" && brandOverrides
    ? {
        ...base,
        splash: { ...base.splash, ...brandOverrides.splash },
        prompts: brandOverrides.prompts ?? base.prompts,
        capabilities: brandOverrides.capabilities ?? base.capabilities,
        header: { ...base.header, ...brandOverrides.header },
      }
    : base;
  const navigate = useNavigate();
  const { sessionId: urlSessionId } = useParams<{ sessionId?: string }>();
  const hasRestoredSession = (loaderData.restoredUIMessages?.length ?? 0) > 0;
  const [started, setStarted] = useState(!!urlSessionId);
  const [selectedPrompt, setSelectedPrompt] = useState<string | undefined>();
  const initialPromptSentRef = useRef(false);
  const [creatorName, setCreatorName] = useState(loaderData.restoredCreatorName ?? "");
  const [activeTab, setActiveTab] = useState<DemoTab>("create");

  const handleSessionChange = useCallback(
    (sessionId: string) => {
      navigate(`/cara/demo/${sessionId}`, { replace: true });
    },
    [navigate],
  );

  const handleStart = useCallback((name: string, prompt?: string) => {
    setCreatorName(name);
    setSelectedPrompt(prompt);
    initialPromptSentRef.current = false;
    setActiveTab("create");
    setStarted(true);
  }, []);

  useEffect(() => {
    if (started && selectedPrompt && !initialPromptSentRef.current) {
      initialPromptSentRef.current = true;
    }
  }, [started, selectedPrompt]);

  if (!started) {
    return <SplashScreen onStart={handleStart} demoAudience={demoAudience} />;
  }

  const tabs: { id: DemoTab; label: string }[] = [
    { id: "create", label: d.header.tabs.create },
    { id: "gallery", label: d.header.tabs.gallery },
  ];

  return (
    <div data-theme="client" className="h-screen bg-background text-foreground flex flex-col">
      {/* ── Header ── */}
      <div className="shrink-0 border-b border-border/50">
        {/* Top row: logo + tabs (desktop) + actions */}
        <div className="flex items-center justify-between px-3 sm:px-4 h-10">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <a href="/" className="transition-opacity hover:opacity-60 shrink-0">
              <CaratzonLogo size={22} className="opacity-70" />
            </a>
            <div className="h-4 w-px bg-border/30 hidden sm:block" />
            <div className="flex items-center gap-1.5 shrink-0">
              <div
                className="h-4 w-4 rounded flex items-center justify-center shrink-0"
                style={{ background: "oklch(0.45 0.08 213 / 0.1)" }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-2.5 w-2.5" style={{ color: "var(--teal-sapphire)" }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z" />
                </svg>
              </div>
              <span className="text-[9px] uppercase tracking-[0.25em] font-medium hidden sm:inline" style={{ color: "var(--teal-sapphire)" }}>
                {d.header.brandLabel}
              </span>
            </div>

            {/* Inline tabs — desktop only */}
            <div className="h-4 w-px bg-border/30 ml-1 hidden sm:block" />
            <nav className="hidden sm:flex items-center gap-0.5 ml-0.5" role="tablist">
              {tabs.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] uppercase tracking-[0.15em] font-medium transition-all duration-150 ${
                      isActive
                        ? "bg-foreground/[0.06] text-foreground"
                        : "text-muted-foreground/50 hover:text-muted-foreground hover:bg-foreground/[0.03]"
                    }`}
                  >
                    <TabIcon tab={tab.id} active={isActive} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <LanguageSwitcher />
            <button
              onClick={() => { setStarted(false); setSelectedPrompt(undefined); navigate("/cara/demo", { replace: true }); }}
              className="text-[10px] text-muted-foreground/40 hover:text-foreground transition-colors px-1.5 sm:px-2 py-1 hidden sm:block"
            >
              {d.header.back}
            </button>
            <a
              href={demoAudience === "brands" ? "/sign-in?role=buyer" : "/sign-in"}
              className="text-[10px] uppercase tracking-[0.18em] px-2.5 sm:px-3.5 py-1.5 transition-all duration-200 hover:brightness-110 whitespace-nowrap"
              style={{
                background: "var(--teal-sapphire)",
                color: "var(--teal-sapphire-fg, #fff)",
              }}
            >
              {d.header.fullAccess}
            </a>
          </div>
        </div>

        {/* Tab bar — mobile only, separate row */}
        <nav className="flex sm:hidden items-center gap-0.5 px-3 pb-1.5" role="tablist">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md text-[10px] uppercase tracking-[0.15em] font-medium transition-all duration-150 ${
                  isActive
                    ? "bg-foreground/[0.06] text-foreground"
                    : "text-muted-foreground/50 hover:text-muted-foreground hover:bg-foreground/[0.03]"
                }`}
              >
                <TabIcon tab={tab.id} active={isActive} />
                {tab.label}
              </button>
            );
          })}
          <button
            onClick={() => { setStarted(false); setSelectedPrompt(undefined); navigate("/cara/demo", { replace: true }); }}
            className="text-[10px] text-muted-foreground/40 hover:text-foreground transition-colors px-2 py-1.5"
          >
            {d.header.back}
          </button>
        </nav>
      </div>

      <div className="flex-1 min-h-0">
        {activeTab === "create" ? (
          <CaraStudio
            demoMode
            mode={demoAudience === "brands" ? "buyer" : "designer"}
            apiUrl="/resources/cara-demo"
            initialPrompt={initialPromptSentRef.current ? undefined : selectedPrompt}
            creatorName={creatorName}
            initialSessionId={urlSessionId}
            onSessionChange={handleSessionChange}
            restoredUIMessages={hasRestoredSession ? loaderData.restoredUIMessages : undefined}
            restoredArtifacts={hasRestoredSession ? loaderData.restoredArtifacts : undefined}
          />
        ) : (
          <DemoGallery onTryIt={() => setActiveTab("create")} />
        )}
      </div>
    </div>
  );
}
